/**
 * Pure helper for extracting the AB test pricebook segment from the
 * `hec_priceTestVariant` cookie. Kept in a separate module so it can be
 * unit-tested without pulling in Cloudflare-specific runtime imports.
 *
 * COM-123 (grafted from PR #918): the value is shopper-controlled (a plain
 * cookie) and fans out into SWR cache-key segments, the SCAPI
 * `c_hec_priceTestVariant` param, and the Builder `userAttributes` cookie —
 * so anything outside the strict variant token format falls back to `""`
 * (treated everywhere as "no variant"). Real variants (`segmentA` /
 * `segmentB`) match and pass through unchanged.
 */
const VALID_PRICE_TEST_VARIANT = /^[A-Za-z0-9_-]*$/;

export const parsePriceTestVariant = (cookieHeader: string): string => {
  const raw =
    cookieHeader
      .split(";")
      .map(c => c.trim())
      .find(c => c.startsWith("hec_priceTestVariant="))
      ?.substring("hec_priceTestVariant=".length) ?? "";
  return VALID_PRICE_TEST_VARIANT.test(raw) ? raw : "";
};

/**
 * Cache-key segment for the AB price-test variant (SFCC-11034). Guest PLP/PDP
 * SWR entries embed SCAPI-enriched prices that vary by the active pricebook, so
 * the variant MUST be part of the cache key — otherwise a variant-assigned
 * shopper's test-pricebook prices would be served to default shoppers (and vice
 * versa). Normalizes the empty/no-variant case to a stable `"default"` token so
 * all non-variant guests share a single entry and each variant gets its own.
 */
export const priceTestVariantCacheSegment = (cookieHeader: string): string =>
  parsePriceTestVariant(cookieHeader) || "default";

/**
 * Client-side read of the active variant from `document.cookie`. Returns `""`
 * both when no variant is set and when there is no DOM (SSR), so callers can
 * treat "no variant" and "server render" identically.
 *
 * Lives here rather than in `dev-panel-utils` because production request paths
 * depend on it: a client-fetched resource route that embeds SCAPI prices must
 * put the variant into its request URL, since the edge cache key is a hash of
 * the URL with cookies stripped (`caching.server.ts` `toCacheKey`). See
 * `app/routes/resources/cio-browse.tsx` (SFCC-11874).
 */
export const readPriceTestVariantCookie = (): string =>
  typeof document === "undefined" ? "" : parsePriceTestVariant(document.cookie);
